import { useForm } from "react-hook-form";
import { ActionButton } from "../../components/Forms/ActionButton";
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useLocation, useNavigate } from "react-router-dom";
import MediaUpload from "../../components/Forms/MediaUpload";
import MultiMediaUploadSection from "../../components/Forms/MultiMediaUploadSection";
import ActiveStatus from "../../components/Common/ActiveStatus";
import { useHeader } from "../../context/HeaderContext";
import { useCreateProduct, useUpdateProduct, useGetProductById, useGetProductCategories } from "../../queries/products";
import { useGetActiveOrganiser } from "../../queries/user";
import { useGetBrands } from "../../queries/brand";
import NewDropdown from "../../components/Forms/NewDropdown";

const CreateProductOrService = () => {

  const navigate = useNavigate();
  const { state } = useLocation();
  const productId = state?.productId || state?._id;

  const [logo, setLogo] = useState(null);
  const [additionalImages, setAdditionalImages] = useState([]);
  const [active, setActive] = useState(true);

  const { setHeaderTitle } = useHeader();

  // API Hooks
  const { data: organiserList = [], isLoading: isOrganiserPending } = useGetActiveOrganiser();
  const { data: brands = [], isLoading: isBrandsPending } = useGetBrands();
  const { data: productCategories = [], isLoading: isCategoriesPending } = useGetProductCategories();
  const { data: productData, isLoading: isProductDataPending } = useGetProductById(productId);
  const { mutateAsync: createProductMutation, isLoading: isCreatingProduct } = useCreateProduct();
  const { mutateAsync: updateProductMutation, isLoading: isUpdatingProduct } = useUpdateProduct();

  // Form setup
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
    trigger,
  } = useForm({
    defaultValues: {
      organizerId: '',
      brandId: '',
      categoryId: '',
      type: '',
      name: '',
      shortDescription: '',
      longDescription: '',
      link: '',
      remarks: '',
    }
  });

  const watchedOrganizerId = watch("organizerId");
  const watchedBrandId = watch("brandId");
  const watchedCategoryId = watch("categoryId");
  const watchedType = watch("type");

  // Transform brands data for dropdown, filter by selected organization
  const brandsData = useMemo(() => {
    let filteredBrands = brands;

    // Filter brands by selected organization if an organization is selected
    if (watchedOrganizerId) {
      filteredBrands = brands.filter(brand =>
        brand.companyId?._id === watchedOrganizerId ||
        brand.ngoId?._id === watchedOrganizerId
      );
    }

    return filteredBrands.map(brand => ({
      _id: brand._id,
      name: brand.brandName,
      // Store additional data for reference
      companyName: brand.companyId?.companyName || brand.ngoId?.organisationName || 'Unknown Organization'
    }));
  }, [brands, watchedOrganizerId]);

  // Transform product categories data for dropdown
  const categoriesData = useMemo(() => {
    return productCategories.map(category => ({
      _id: category._id,
      name: category.name,
      value: category._id,
      label: category.name
    }));
  }, [productCategories]);

  // Use ref to track previous organization ID to avoid clearing brand on every brand change
  const prevOrganizerId = useRef(watchedOrganizerId);

  // Clear brand selection when organization changes (except during initial load/edit)
  useEffect(() => {
    const currentOrgId = watchedOrganizerId;
    const previousOrgId = prevOrganizerId.current;

    // Only clear brand if organization actually changed and we're not in edit mode
    if (currentOrgId !== previousOrgId && !productId && watchedBrandId) {
      setValue('brandId', '');
    }

    // Update ref with current value
    prevOrganizerId.current = currentOrgId;
  }, [watchedOrganizerId, setValue, productId, watchedBrandId]);

  // Set header title based on mode
  useEffect(() => {
    if (productId) {
      setHeaderTitle('Edit Product/Service');
    } else {
      setHeaderTitle('Create Product/Service');
    }
    return () => {
      setHeaderTitle('');
    };
  }, [setHeaderTitle, productId]);

  // Populate form when editing
  useEffect(() => {
    setTimeout(() => {
      if (productData && productId) {
        console.log('📝 Populating form with product data:', productData);
        setActive(productData.isActive !== undefined ? productData.isActive : productData.active);

        // Handle organizerId - check if it's directly available, otherwise derive from brand
        let organizerId = '';
        if (productData.companyId) {
          organizerId = typeof productData.companyId === 'object' && productData.companyId?._id
            ? productData.companyId._id
            : productData.companyId;
        } else if (productData.ngoId) {
          organizerId = typeof productData.ngoId === 'object' && productData.ngoId?._id
            ? productData.ngoId._id
            : productData.ngoId;
        } else if (productData.brandId && brands.length > 0) {
          // If no direct organization but brandId exists, find organization from brand
          const brandData = brands.find(brand =>
            brand._id === productData.brandId ||
            (typeof productData.brandId === 'object' && brand._id === productData.brandId?._id)
          );
          if (brandData) {
            organizerId = brandData.companyId?._id || brandData.ngoId?._id || '';
          }
        }

        // Handle brandId
        const brandIdValue = typeof productData.brandId === 'object' && productData.brandId?._id
          ? productData.brandId._id
          : productData.brandId || '';

        setValue('organizerId', organizerId);
        setValue('brandId', brandIdValue);
        setValue('categoryId', productData.categoryId?._id || productData.categoryId || '');
        setValue('type', productData.type || '');
        setValue('name', productData.name || '');
        setValue('shortDescription', productData.shortDescription || '');
        setValue('longDescription', productData.longDescription || '');
        setValue('link', productData.link || '');
        setValue('remarks', productData.remarks || '');

        // Handle images
        if (productData.imageUrl) {
          setLogo(productData.imageUrl);
        }
        if (productData.additionalImages && Array.isArray(productData.additionalImages)) {
          setAdditionalImages(productData.additionalImages);
        }
      }
    }, 200);
  }, [productData, productId, setValue, brands]);

  const onSubmit = (data) => {
    // Validate required fields
    if (!data.organizerId) {
      toast.error('Please select a Company/NGO');
      return;
    }
    if (!data.type) {
      toast.error('Please select a Type (Product/Service)');
      return;
    }

    // Find the selected organizer to determine if it's Company or NGO
    const selectedOrganizer = organiserList.find(org => org._id === data.organizerId);

    const payload = {
      isActive: active,
      brandId: data.brandId || null,
      categoryId: data.categoryId || null,
      type: data.type,
      name: data.name,
      shortDescription: data.shortDescription || '',
      longDescription: data.longDescription || '',
      link: data.link,
      remarks: data.remarks || '',
    };

    // Set either companyId or ngoId based on organizer type
    if (selectedOrganizer?.organiserType === 'Company') {
      payload.companyId = data.organizerId;
      payload.ngoId = null;
    } else if (selectedOrganizer?.organiserType === 'NGO') {
      payload.ngoId = data.organizerId;
      payload.companyId = null;
    }

    // Include existing image metadata when editing (if logo wasn't changed)
    if (productId && productData) {
      if (!logo || !(logo instanceof File)) {
        // Logo wasn't changed, preserve existing metadata
        payload.imageId = productData.imageId;
        payload.imageVersion = productData.imageVersion;
        payload.imageUrl = productData.imageUrl;
      } else {
        // Logo was changed, pass imageId so updateImage can be used
        payload.imageId = productData.imageId;
      }
    }

    console.log('Submitting payload:', payload);

    if (productId) {
      updateProductMutation({
        id: productId,
        data: payload,
        logo: logo,
        additionalImages: additionalImages,
        toast,
        navigate
      }, {
        onSuccess: () => {
          toast.success("Product/Service updated successfully");
          setTimeout(() => {
            navigate('/products/products-main/view-products');
          }, 2000);
        },
        onError: (err) => {
          console.error(err);
          toast.error("An error occurred while updating the product/service!");
        }
      });
    } else {
      createProductMutation({
        data: payload,
        logo: logo,
        additionalImages: additionalImages,
        toast,
        navigate
      }, {
        onSuccess: () => {
          toast.success("Product/Service created successfully");
          setTimeout(() => {
            navigate('/products/products-main/view-products');
          }, 2000);
        },
        onError: (err) => {
          console.error(err);
          toast.error("An error occurred while creating the product/service!");
        }
      });
    }
  };

  if (isProductDataPending || isOrganiserPending || isBrandsPending || isCategoriesPending) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p className="text-lg font-semibold">Loading...</p>
      </div>
    );
  }

  return (
    <>
      <form className="flex flex-col w-full font-semibold" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex items-center justify-end gap-3">
          <ActiveStatus
            active={active}
            setActive={setActive}
            isCreateForm={true}
          />
        </div>

        <section className="flex flex-col justify-center px-5 pt-4 py-9 gap-3 w-full bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)]">
          <div className="flex items-center justify-between">
            <p className="text-base font-bold">Product/Service Information</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            <div className="flex flex-col gap-3 w-full">
              {/* Company/NGO */}
              <div className='flex flex-col gap-1 w-full'>
                <label htmlFor="organizerId" className="gap-1 self-stretch w-full text-gray-900 text-[14px] font-[600]">
                  <span className="text-red text-xs inline">* </span>
                  Client
                </label>
                <NewDropdown
                  name={"organizerId"}
                  dataList={organiserList}
                  value={watchedOrganizerId}
                  placeholder={'Select Company/NGO'}
                  onChange={(e) => {
                    setValue("organizerId", e.value);
                    if (e.value) {
                      trigger("organizerId");
                    }
                  }}
                />
                {errors.organizerId && <p className="text-red text-sm mt-1">{errors.organizerId.message}</p>}
              </div>

              {/* Brand */}
              <div className='flex flex-col gap-1 w-full'>
                <label htmlFor="brandId" className="gap-1 self-stretch w-full text-gray-900 text-[14px] font-[600]">
                  Brand
                </label>
                <NewDropdown
                  name={"brandId"}
                  dataList={brandsData}
                  value={watchedBrandId}
                  placeholder={'Select Brand (Optional)'}
                  onChange={(e) => {
                    setValue("brandId", e.value);
                  }}
                />
                {errors.brandId && <p className="text-red text-sm mt-1">{errors.brandId.message}</p>}
              </div>
              {/* Category */}
              <div className='flex flex-col gap-1'>
                <label htmlFor="categoryId" className="gap-1 self-stretch w-full text-gray-900 text-[14px] font-[600]">
                  Category
                </label>
                <NewDropdown
                  name={"categoryId"}
                  dataList={categoriesData}
                  value={watchedCategoryId}
                  placeholder={'Select Category (Optional)'}
                  onChange={(e) => {
                    setValue("categoryId", e.value);
                  }}
                />
                {errors.categoryId && <p className="text-red text-sm mt-1">{errors.categoryId.message}</p>}
              </div>
              {/* Type */}
              <div className='flex flex-col gap-1'>
                <label htmlFor="type" className="gap-1 self-stretch w-full text-gray-900 text-[14px] font-[600]">
                  <span className="text-red text-xs inline">* </span>
                  Type
                </label>
                <NewDropdown
                  name={"type"}
                  dataList={[
                    { value: 'Product', label: 'Product' },
                    { value: 'Service', label: 'Service' }
                  ]}
                  value={watchedType}
                  placeholder={'Select Type'}
                  onChange={(e) => {
                    setValue("type", e.value);
                    if (e.value) {
                      trigger("type");
                    }
                  }}
                />
                {errors.type && <p className="text-red text-sm mt-1">{errors.type.message}</p>}
              </div>
              {/* Product/Service Name */}
              <div className="flex flex-col shrink-0 basis-0 w-full">
                <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">
                  <span className="text-red text-xs inline">*</span> Product/Service Name
                </label>
                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                  <input
                    type="text"
                    placeholder="Enter Product/Service Name"
                    {...register("name", {
                      required: "Product/Service Name is required",
                      minLength: { value: 2, message: "Name must be at least 2 characters" }
                    })}
                    className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.name ? "border-red-500" : ""}`}
                  />
                </div>
                {errors.name && <p className="text-red text-sm">{errors.name.message}</p>}
              </div>
            </div>

            {/* Logo */}
            <div className="flex flex-col gap-2">
              <MediaUpload
                clearMedia={false}
                label={"Logo"}
                existingImage={logo}
                setImageUpload={setLogo}
                stateImage={logo}
                setStateImageUpload={setLogo}
                useFileObject={true}
              />
            </div>

            {/* Short Description */}
            <div className="flex flex-col shrink-0 basis-0 w-full">
              <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">
                Short Description
              </label>
              <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                <textarea
                  placeholder="Enter Short Description"
                  {...register("shortDescription")}
                  className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base min-h-[100px] resize-y ${errors.shortDescription ? "border-red-500" : ""}`}
                />
              </div>
              {errors.shortDescription && <p className="text-red text-sm">{errors.shortDescription.message}</p>}
            </div>

            {/* Long Description */}
            <div className="flex flex-col shrink-0 basis-0 w-full">
              <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">
                Long Description
              </label>
              <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                <textarea
                  placeholder="Enter Long Description"
                  {...register("longDescription")}
                  className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base min-h-[100px] resize-y ${errors.longDescription ? "border-red-500" : ""}`}
                />
              </div>
              {errors.longDescription && <p className="text-red text-sm">{errors.longDescription.message}</p>}
            </div>

            {/* Link */}
            <div className="flex flex-col shrink-0 basis-0 col-span-2 w-full">
              <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">
                {/* <span className="text-red text-xs inline">*</span>  */}
                Link
              </label>
              <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                <input
                  type="text"
                  placeholder="Enter Link (This link will be used to redirect user)"
                  {...register("link", {
                    // required: "Link is required",
                    required: false,
                    pattern: {
                      value: /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/,
                      message: "Please enter a valid URL"
                    }
                  })}
                  className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.link ? "border-red-500" : ""}`}
                />
              </div>
              {errors.link && <p className="text-red text-sm">{errors.link.message}</p>}
            </div>

            {/* Remarks */}
            <div className="flex flex-col shrink-0 basis-0 col-span-2 w-full">
              <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">
                Remarks
              </label>
              <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                <textarea
                  placeholder="Enter Remarks"
                  {...register("remarks")}
                  className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base min-h-[80px] resize-y ${errors.remarks ? "border-red-500" : ""}`}
                />
              </div>
              {errors.remarks && <p className="text-red text-sm">{errors.remarks.message}</p>}
            </div>

            {/* Additional Images */}
            <div className="col-span-2">
              <MultiMediaUploadSection
                title="Images"
                files={additionalImages}
                setFiles={setAdditionalImages}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-10 flex gap-5 items-center justify-between">
            <ActionButton
              variant="reset"
              text="Reset"
              handleSubmit={() => {
                reset();
                setActive(true);
                setLogo(null);
                setAdditionalImages([]);
              }}
            />
            <div className="flex items-center gap-3">
              <ActionButton
                variant="cancel"
                text="Cancel"
                handleSubmit={() => navigate('/products/products-main/view-products')}
              />
              <ActionButton
                variant="submit"
                text={productId ? "Update" : "Create"}
                handleSubmit={() => { }}
                disabled={isCreatingProduct || isUpdatingProduct}
              />
            </div>
          </div>
        </section>

        <ToastContainer
          position="top-right"
        />
      </form>
    </>
  );
}

export default CreateProductOrService;